import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert, Box, Divider, Error, IconCopyButton, Typography } from 'shared/ui'
import { LINK_CUSTOM_SHORT_URL } from 'shared/constants/links'
import type { UrlShortenerModalStore } from 'pages/settings/pages/urlShortener/store/urlShortenerModalStore'
import { UrlShortenerStatus } from 'pages/settings/pages/urlShortener/ui/UrlShortenerStatus/UrlShortenerStatus'
import styles from './styles.module.scss'

export type IVerifyCustomDomainModalContentProps = {
  store: UrlShortenerModalStore
}

export const VerifyCustomDomainModalContent = observer(
  ({ store }: IVerifyCustomDomainModalContentProps) => {
    const { domainName, clipboardRequiredDataText, urlItem } = store

    return (
      <Box marginTop={'-9px'}>
        <Alert
          marginBottom={30}
          item={{
            type: 'infoBlue',
            desc: 'Map your domain or subdomain to use for short URLs on Salesmsg.',
          }}
          rightActions={[
            {
              variant: 'link',
              type: 'primary',
              text: 'Learn more',
              link: LINK_CUSTOM_SHORT_URL,
            },
          ]}
        />

        <Box display={'flex'} gap={'8px'} marginBottom={'19px'}>
          <Typography ariaLabel={'counter'} variant={'body-md-medium'} className={styles.stepBox}>
            1
          </Typography>
          <Box paddingTop={'3px'}>
            <Typography
              ariaLabel={'dnsSettingsTitle'}
              variant={'body-md-semibold'}
              marginBottom={'4px'}
              tag={'div'}
            >
              Open your DNS settings
            </Typography>
            <Typography
              ariaLabel={'dnsSettingsDescription'}
              variant={'body-md-regular'}
              color={'var(--content-primary-secondary)'}
            >
              Log in to your domain provider (like GoDaddy, Google Domains, or Cloudflare) and open
              the DNS record settings.
            </Typography>
          </Box>
        </Box>

        <Box display={'flex'} gap={'8px'}>
          <Typography ariaLabel={'counter'} variant={'body-md-medium'} className={styles.stepBox}>
            2
          </Typography>
          <Box paddingTop={'3px'} paddingRight={'10px'}>
            <Typography
              ariaLabel={'dnsRecordTitle'}
              variant={'body-md-semibold'}
              marginBottom={'4px'}
              tag={'div'}
            >
              Add this DNS record
            </Typography>
            <Typography
              ariaLabel={'dnsRecordDescription'}
              variant={'body-md-regular'}
              color={'var(--content-primary-secondary)'}
            >
              Copy the required data below and paste it into the matching field on your provider’s
              site.
            </Typography>
          </Box>
        </Box>

        <Divider style={{ borderColor: 'var(--border-neutral-default)', margin: '32px 0 24px' }} />

        <Box marginBottom={'29px'}>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            marginBottom={'4px'}
          >
            <Typography ariaLabel={'domainName'} variant={'heading-md'} ellipsis>
              {domainName}
            </Typography>
            {urlItem && (
              <div style={{ whiteSpace: 'nowrap' }}>
                <UrlShortenerStatus item={urlItem} />
              </div>
            )}
          </Box>
          {urlItem?.failed_reason && <Error type={'text'} error={urlItem.failed_reason} />}
        </Box>

        <Typography
          marginBottom={'12px'}
          ariaLabel={'recordType'}
          variant={'text-input-label-small'}
          color={'var(--content-primary-tertiary)'}
          tag={'div'}
        >
          Record Type A
        </Typography>

        <div className={styles.recordsWrap}>
          <Box display={'flex'} alignItems={'center'} className={styles.record}>
            <Typography
              ariaLabel={'domainName'}
              variant={'text-input-field-r'}
              color={'var(--content-primary-tertiary)'}
            >
              Host:
            </Typography>
            <Typography
              ariaLabel={'domainName'}
              variant={'text-input-field-r'}
              style={{ paddingLeft: '4px' }}
              className={styles.recordLabel}
              ellipsis
            >
              {domainName}
            </Typography>
            <IconCopyButton value={domainName} className={styles.copyButton} />
          </Box>
          <Divider style={{ borderColor: 'var(--border-neutral-default)' }} />
          <Box display={'flex'} alignItems={'center'} className={styles.record}>
            <Typography
              ariaLabel={'domainName'}
              variant={'text-input-field-r'}
              color={'var(--content-primary-tertiary)'}
            >
              Required data:
            </Typography>
            <Typography
              ariaLabel={'requiredData'}
              variant={'text-input-field-r'}
              style={{ paddingLeft: '4px' }}
              className={styles.recordLabel}
              ellipsis
            >
              {clipboardRequiredDataText}
            </Typography>
            <IconCopyButton value={clipboardRequiredDataText} className={styles.copyButton} />
          </Box>
        </div>

        <Typography
          ariaLabel={'duration'}
          variant={'text-input-helper'}
          color={'var(--content-primary-tertiary)'}
          tag={'div'}
          marginTop={'14px'}
          marginBottom={'2px'}
        >
          It can take up to 48 hours for DNS records to update.
        </Typography>
      </Box>
    )
  }
)
